import { toastError } from "@hubrise/react-components"
import type { ValidationError } from "@hubrise/react-components"
import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { useConfig } from "shared/components/ConfigContext"
import useImageUpload from "shared/hooks/useImageUpload"
import catalogExporter from "shared/hubrise/catalogExporter"
import catalogImporter, { ImportedCatalog } from "shared/hubrise/catalogImporter"
import { IAccount, ICatalog, ICategory, IDeal, IDiscount, IProduct } from "shared/hubrise/models"
import { axiosServer } from "shared/utils/server"

type Error = AxiosError<ValidationError>

type Mutation<Variables, Data> = (
  options?: UseMutationOptions<Data, Error, Variables>,
) => UseMutationResult<Data, Error, Variables>

type UseMutation = <Data, Variables, Context>(
  options: UseMutationOptions<Data, Error, Variables, Context>,
) => UseMutationResult<Data, Error, Variables, Context>

const useMutationWithErrorHandler: UseMutation = (options) => {
  const { t } = useTranslation()

  return useMutation({
    onError: (error) => {
      const humanError = () => {
        const data = error.response?.data
        if (!data) {
          return `HTTP code ${error.response?.status}`
        } else if (data.errors !== undefined) {
          return JSON.stringify(data.errors)
        } else if (data.message !== undefined) {
          return data.message
        } else {
          return JSON.stringify(data)
        }
      }

      toastError(t("general.error"), t("general.server_returned_error", { error: humanError() }))
    },
    ...options,
  })
}

export const useCreateCatalogMutation: Mutation<{ name: string; locationHrId?: string }, void> = (options = {}) => {
  const { appInstanceId } = useConfig()
  const queryClient = useQueryClient()

  return useMutationWithErrorHandler({
    ...options,
    mutationFn: async ({ name, locationHrId }) => {
      const url = locationHrId === undefined ? "/api/account/catalogs" : `/api/locations/${locationHrId}/catalogs`
      await axiosServer<{ id: string }>("POST", url, appInstanceId, { data: { name } })
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["GET", "/api/catalogs"] })
      options.onSuccess?.(data, variables, context)
    },
  })
}

export const useDeleteCatalogMutation: Mutation<{ catalogHrId: string }, void> = (options = {}) => {
  const { appInstanceId } = useConfig()
  const queryClient = useQueryClient()

  return useMutationWithErrorHandler({
    ...options,
    mutationFn: async ({ catalogHrId }) => {
      await axiosServer<void>("DELETE", `/api/catalogs/${catalogHrId}`, appInstanceId)
    },
    onSuccess: async (data, variables, context) => {
      queryClient.removeQueries({ queryKey: ["GET", `/api/catalogs/${variables.catalogHrId}`] })
      await queryClient.invalidateQueries({ queryKey: ["GET", "/api/catalogs"] })
      options.onSuccess?.(data, variables, context)
    },
  })
}

export const useDuplicateCatalogMutation: Mutation<
  { catalogOrId: ICatalog | string; name: string; locationHrId?: string },
  ICatalog
> = (options = {}) => {
  const { appInstanceId } = useConfig()
  const queryClient = useQueryClient()
  const imageUpload = useImageUpload()

  return useMutationWithErrorHandler({
    ...options,
    mutationFn: async ({ catalogOrId, name, locationHrId }) => {
      // Fetch currency
      const {
        data: { currency },
      } = await axiosServer<IAccount>("GET", "/api/account", appInstanceId)

      // Fetch catalog to duplicate, if not provided
      let catalog: ICatalog
      if (typeof catalogOrId === "string") {
        const { data } = await axiosServer<ImportedCatalog>("GET", `/api/catalogs/${catalogOrId}`, appInstanceId)
        catalog = catalogImporter(data, currency)
      } else {
        catalog = catalogOrId
      }

      // Create catalog
      const url = locationHrId === undefined ? "/api/account/catalogs" : `/api/locations/${locationHrId}/catalogs`
      const {
        data: { id: newCatalogHrId },
      } = await axiosServer<{ id: string }>("POST", url, appInstanceId, { data: { name } })

      // Duplicate images
      const duplicateImages = async (imagesHrIds: Array<string>): Promise<Array<string>> => {
        return Promise.all(
          imagesHrIds.map(async (imageHrId) => {
            const { data: blob } = await axiosServer<Blob>(
              "GET",
              `/images/${catalog.hrId}/${imageHrId}`,
              appInstanceId,
              { responseType: "blob" },
            )
            return await imageUpload(blob, newCatalogHrId)
          }),
        )
      }

      const duplicateAllImages = async <TResource extends ICategory | IDeal | IDiscount | IProduct>(
        resources: Array<TResource>,
      ): Promise<Array<TResource>> => {
        return await Promise.all(
          resources.map(async (resource) => ({
            ...resource,
            imageHrIds: await duplicateImages(resource.imageHrIds),
          })),
        )
      }

      await Promise.all([
        (async () => (catalog.data.categories = await duplicateAllImages(catalog.data.categories)))(),
        (async () => (catalog.data.deals = await duplicateAllImages(catalog.data.deals)))(),
        (async () => (catalog.data.discounts = await duplicateAllImages(catalog.data.discounts)))(),
        (async () => (catalog.data.products = await duplicateAllImages(catalog.data.products)))(),
      ])

      // Upload catalog
      const exportedCatalog = catalogExporter(catalog)
      const { data: newCatalog } = await axiosServer<ImportedCatalog>(
        "PUT",
        `/api/catalogs/${newCatalogHrId}`,
        appInstanceId,
        {
          data: exportedCatalog,
        },
      )

      return catalogImporter(newCatalog, catalog.currency)
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["GET", "/api/catalogs"] })
      options.onSettled?.(data, error, variables, context)
    },
  })
}

export const useRenameCatalogMutation: Mutation<{ name: string; catalogHrId: string }, void> = (options = {}) => {
  const { appInstanceId } = useConfig()
  const queryClient = useQueryClient()

  return useMutationWithErrorHandler({
    ...options,
    mutationFn: async ({ name, catalogHrId }) => {
      // Use the PATCH endpoint which does not return the full catalog
      await axiosServer("PATCH", `/api/catalogs/${catalogHrId}`, appInstanceId, { data: { name } })
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ["GET", "/api/catalogs"] })

      // Update the catalog in the cache to avoid a refetch
      const { name, catalogHrId } = variables
      const queryKey = ["GET", `/api/catalogs/${catalogHrId}`]
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData<ICatalog>(queryKey, (oldCatalog) => (oldCatalog ? { ...oldCatalog, name } : undefined))

      options.onSuccess?.(data, variables, context)
    },
  })
}

export const useUploadCatalogMutation: Mutation<ICatalog, ICatalog> = (options = {}) => {
  const { appInstanceId } = useConfig()

  return useMutationWithErrorHandler({
    ...options,
    mutationFn: async (catalog) => {
      const exportedCatalog = catalogExporter(catalog)
      const { data: newCatalog } = await axiosServer<ImportedCatalog>(
        "PUT",
        `/api/catalogs/${catalog.hrId}`,
        appInstanceId,
        { data: exportedCatalog },
      )
      return catalogImporter(newCatalog, catalog.currency)
    },
  })
}
